import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  api,
  type KnowledgeAutoRegistrationPreference,
} from "../lib/api";
import { logError } from "../lib/errors";

type PreferenceKey = keyof KnowledgeAutoRegistrationPreference;
type PreferenceStatus = "loading" | "loaded" | "error";

export function KnowledgeAutoRegistrationSettings() {
  const [preference, setPreference] = useState<KnowledgeAutoRegistrationPreference | null>(null);
  const [status, setStatus] = useState<PreferenceStatus>("loading");
  const [saving, setSaving] = useState<Partial<Record<PreferenceKey, true>>>({});
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const savingRef = useRef(new Set<PreferenceKey>());

  const loadPreference = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setStatus("loading");
    setError(null);

    try {
      const response = await api.getKnowledgeAutoRegistrationPreference();
      if (requestIdRef.current !== requestId) return;
      setPreference(response);
      setStatus("loaded");
    } catch (loadError) {
      if (requestIdRef.current !== requestId) return;
      logError(loadError);
      setPreference(null);
      setStatus("error");
      setError("自動追加の設定を確認できませんでした。");
    }
  }, []);

  useEffect(() => {
    void loadPreference();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadPreference]);

  const togglePreference = async (key: PreferenceKey) => {
    if (!preference || savingRef.current.has(key)) return;

    const previousValue = preference[key];
    const nextValue = !previousValue;
    savingRef.current.add(key);
    setSaving((current) => ({ ...current, [key]: true }));
    setError(null);
    setPreference((current) => current ? { ...current, [key]: nextValue } : current);

    try {
      const saved = await api.updateKnowledgeAutoRegistrationPreference(
        { [key]: nextValue }
      );
      setPreference((current) => current ? { ...current, [key]: saved[key] } : saved);
    } catch (saveError) {
      logError(saveError);
      setPreference((current) => current ? { ...current, [key]: previousValue } : current);
      setError("自動追加の設定を保存できませんでした。通信状況を確認して、もう一度お試しください。");
    } finally {
      savingRef.current.delete(key);
      setSaving((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <section aria-label="自動追加">
      <h3 className="text-sm font-black text-[#5D4D46]">自動追加</h3>
      <p className="mt-1 text-xs leading-5 text-[#7C6A62]">
        社長メモや企業リサーチ、深掘りレポートを、ナレッジライブラリへ自動で追加します。
      </p>

      {status === "loading" ? (
        <p className="mt-4 rounded-xl bg-[#FDF8F5] px-3 py-3 text-xs font-bold text-[#8F7468]">
          設定を確認中...
        </p>
      ) : status === "error" ? (
        <div className="mt-4 rounded-xl bg-[#FFF0EB] px-3 py-3">
          <p role="alert" className="text-xs font-bold text-[#B4533A]">
            {error}
          </p>
          <button
            type="button"
            onClick={() => void loadPreference()}
            className="mt-3 min-h-[40px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-xs font-black text-[#6B5851]"
          >
            再試行
          </button>
        </div>
      ) : preference ? (
        <>
          <div className="mt-4 grid gap-3">
            <div className="flex flex-col gap-3 rounded-xl bg-[#FDF8F5] p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-black text-[#5D4D46]">社長メモを自動追加</p>
                <p className="mt-1 break-words text-xs leading-5 text-[#7C6A62]">
                  新しく作成・処理した社長メモを、AIで使える資料として自動追加します。
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={preference.autoAddExecutiveMemos}
                aria-label="社長メモを自動追加"
                disabled={Boolean(saving.autoAddExecutiveMemos)}
                onClick={() => void togglePreference("autoAddExecutiveMemos")}
                className="min-h-[40px] min-w-[72px] shrink-0 rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-xs font-black text-[#5D4D46] disabled:opacity-60"
              >
                {saving.autoAddExecutiveMemos ? "保存中" : preference.autoAddExecutiveMemos ? "ON" : "OFF"}
              </button>
            </div>
            <div className="flex flex-col gap-3 rounded-xl bg-[#FDF8F5] p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-black text-[#5D4D46]">企業リサーチの分析結果を自動追加</p>
                <p className="mt-1 break-words text-xs leading-5 text-[#7C6A62]">
                  「詳しく調べる」や目的別分析の完了結果を、AIで使える資料として自動追加します。企業情報の確認（概要）のみでは追加されません。
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={preference.autoAddCompanyResearch}
                aria-label="企業リサーチの分析結果を自動追加"
                disabled={Boolean(saving.autoAddCompanyResearch)}
                onClick={() => void togglePreference("autoAddCompanyResearch")}
                className="min-h-[40px] min-w-[72px] shrink-0 rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-xs font-black text-[#5D4D46] disabled:opacity-60"
              >
                {saving.autoAddCompanyResearch ? "保存中" : preference.autoAddCompanyResearch ? "ON" : "OFF"}
              </button>
            </div>
            <div className="flex flex-col gap-3 rounded-xl bg-[#FDF8F5] p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-black text-[#5D4D46]">深掘りレポートを自動追加</p>
                <p className="mt-1 break-words text-xs leading-5 text-[#7C6A62]">
                  新しく作成した深掘りレポートを、AIで使える資料として自動追加します。
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={preference.autoAddDeepReports}
                aria-label="深掘りレポートを自動追加"
                disabled={Boolean(saving.autoAddDeepReports)}
                onClick={() => void togglePreference("autoAddDeepReports")}
                className="min-h-[40px] min-w-[72px] shrink-0 rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-xs font-black text-[#5D4D46] disabled:opacity-60"
              >
                {saving.autoAddDeepReports ? "保存中" : preference.autoAddDeepReports ? "ON" : "OFF"}
              </button>
            </div>
          </div>
          <div className="mt-3 space-y-1 text-xs leading-5 text-[#7C6A62]">
            <p>OFFにしても、すでに登録済みの資料は削除されません。</p>
            <p>ONに戻しても、OFF中に作成した過去の資料は自動では追加されません。</p>
          </div>
          {error ? (
            <p role="alert" className="mt-3 rounded-xl bg-[#FFF0EB] px-3 py-2 text-xs font-bold text-[#B4533A]">
              {error}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
